import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Building2, Globe, Users, Shield, Leaf, Clock, Award, Target } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';

const AboutPage = () => {
  const { t } = useTranslation();
  
  // Breadcrumb schema for search results
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'About' }
  ]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <SEOHead 
        title="About TradeHub24 | Virtual Tradeshow Platform"
        description="Learn about TradeHub24's mission to revolutionize trade shows with virtual exhibitions. Connect exhibitors and buyers worldwide."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/about`}
        structuredData={breadcrumbSchema}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Building2 className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('about.title')}</h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('about.mission_title')}</h2>
              <p className="text-slate-600 mb-4">
                {t('about.mission_text_1')}
              </p>
              <p className="text-slate-600 mb-4">
                {t('about.mission_text_2')}
              </p>
              <p className="text-slate-600">
                {t('about.mission_text_3')}
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-sm border border-slate-200">
              <Target className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-4">{t('about.vision_title')}</h3>
              <p className="text-slate-600">
                {t('about.vision_text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{t('about.why_virtual_title')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('about.extended_duration_title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.extended_duration_text')}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="bg-accent/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('about.increased_traffic_title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.increased_traffic_text')}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Leaf className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('about.environmental_title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.environmental_text')}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('about.global_access_title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.global_access_text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{t('about.values_title')}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <Shield className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('about.trust_title')}</h3>
              <p className="text-slate-600">
                {t('about.trust_text')}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <Award className="h-10 w-10 text-accent mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('about.quality_title')}</h3>
              <p className="text-slate-600">
                {t('about.quality_text')}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <Users className="h-10 w-10 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('about.community_title')}</h3>
              <p className="text-slate-600">
                {t('about.community_text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('about.cta_title')}</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('about.cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth">
              <Button size="lg" variant="secondary">
                {t('about.create_account')}
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white hover:bg-white hover:text-primary">
                {t('about.contact_sales')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
